import React, { useCallback, useEffect, useState } from 'react';
import { PageContainer } from '../../components/pageContainer/PageContainer';
import {
    setCoreIframeHtml,
    setPreviousAuthStateForPayments,
    useAppStatePaymentSelector,
} from './paymentSlice';
import styles from './Payment.module.scss';
import { useTranslation } from 'react-i18next';
import { PanelRow } from '../../components/panel/PanelRow';
import { Panel } from '../../components/panel/Panel';
import { useDispatch } from 'react-redux';
import { getCoreIframeURL } from './paymentActionCreators';
import { Button } from '../../components/button/Button';
import { useHistory } from 'react-router-dom';
import { axios } from '../../services/axiosService';
import { useQuery } from '../../hooks/useQuery';
import { useAuthSelector } from '../auth/authSlice';
import { Spinner } from '../../components/spinner/Spinner';
import { useFeatureFlag } from '../featureFlags/FeatureFlag';
import { openMessageModal } from '../../components/statusModal/messageModalActionCreators';
import { CoreBillPayForm } from '../../components/coreIframe/CoreBillPayForm';
import { PaymentEventCallType } from './CorePayment';
import { config } from '../../config';
import { firebaseService } from '../../services/firebaseService';

interface ItemizedBillItem {
    applicationName: string;
    applicationNumber: string;
    applicationFee?: number;
}

export const PaymentLPI = () => {
    const dispatch = useDispatch();
    const lpiFF = useFeatureFlag('EnableLPI');
    const history = useHistory();
    const { t } = useTranslation();
    const [itemizedBill, setItemizedBill] = useState<ItemizedBillItem[]>([]);
    const querystring = useQuery();
    const [amount, setAmount] = useState<number>(0);
    const { isAuthenticated } = useAuthSelector(s => s);
    const { coreIframeHtml } = useAppStatePaymentSelector(state => state);
    const [loadingPayment, setLoadingPayment] = useState(true);
    const [loading, setLoading] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [firstLoad, setFirstLoad] = useState(true);
    const topOfIframeRef = React.useRef<HTMLDivElement>(null);

    const activityId = querystring.get('activityId') || undefined;
    const english = localStorage.getItem('language') !== 'es';

    // Initialize auth state and clear iframe on mount
    useEffect(() => {
        dispatch(setPreviousAuthStateForPayments(isAuthenticated));
        dispatch(setCoreIframeHtml(''));
    }, [dispatch, isAuthenticated]);

    // Fetch itemized bill and request CORE iframe
    useEffect(() => {
        if (!activityId) return;

        axios.get(`/lpiPayment/${activityId}`)
            .then((res) => {
                setLoadingPayment(false);
                setAmount(res.data.amount ?? 0);
                setItemizedBill(res.data.itemizedBill);
                setLoading(true);
                dispatch(
                    getCoreIframeURL(
                        'SINGLE',
                        res.data.accountNumber,
                        res.data.amount,
                        'BUILDING',
                        activityId,
                        english,
                    ),
                );
            }).catch((err) => {
                setLoadingPayment(false);
                dispatch(openMessageModal(null, 'error'));
                console.error(err);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId]);

    const scrollToTopOfIFrame = useCallback(
        (diff) => {
            const iframe = document.getElementById('billPayIframe');
            if (iframe) {
                const iframeTop = topOfIframeRef.current?.offsetTop! - diff;
                setTimeout(() => {
                    window.scrollTo({
                        top: iframeTop,
                        behavior: 'smooth',
                    });
                }, 250);
                if (firstLoad) {
                    setFirstLoad(false);
                }
            }
        },
        [firstLoad],
    );

    const processEvent = useCallback(
        (event) => {
            const data = event?.data;
            const callType = data?.calltype;
            const contentHeight = data?.contentHeight;

            if (contentHeight) {
                const iframe = document.getElementById('billPayIframe');
                if (iframe) {
                    iframe.style.height = contentHeight + 'px';
                }
            }

            if (
                callType === PaymentEventCallType.PAYMENTPROCESS_ERROR &&
                event.origin === config.core.iframeOriginUrl
            ) {
                const iframe = document.getElementById('billPayIframe');
                if (iframe) {
                    scrollToTopOfIFrame(110);
                    iframe.style.height = '600px';
                    iframe.setAttribute('scrolling', 'yes');
                }
            }

            if (callType === PaymentEventCallType.OVERLAY_ON) {
                setLoading(true);
            }

            if (callType === PaymentEventCallType.OVERLAY_OFF) {
                setLoading(false);
            }

            if (callType === PaymentEventCallType.PAYMENT_SUCCESS) {
                setPaymentSuccess(true);
                const iframe = document.getElementById('billPayIframe');
                if (iframe) {
                    scrollToTopOfIFrame(110);
                    iframe.setAttribute('scrolling', 'yes');
                }
                firebaseService
                    .app()
                    .analytics()
                    .logEvent('lpi_payment_success', {
                        platform: 'web',
                    });
            }
        },
        [scrollToTopOfIFrame],
    );

    // Listen for CORE postMessage events
    useEffect(() => {
        window.addEventListener('message', processEvent, false);
        return () => {
            window.removeEventListener('message', processEvent, false);
        };
    }, [processEvent]);

    // Scroll to iframe on first load
    useEffect(() => {
        if (coreIframeHtml && firstLoad) {
            scrollToTopOfIFrame(125);
        }
    }, [coreIframeHtml, firstLoad, scrollToTopOfIFrame]);

    return (
        loadingPayment || !lpiFF?.enabled ?
        <Spinner fullPage isLoading={loadingPayment} />
        :
        <PageContainer style={isAuthenticated ? {} : { maxWidth: 700, width: '100%' }}>
            <Spinner isLoading={loading} fullPage />
            <div className="headerContainer">
                <h2>{t('Payment.Payment')}</h2>
            </div>

            {isAuthenticated ?
                <PanelRow>
                    <Panel>
                        <h3>{t('Payment.ItemizedBill')}</h3>
                        <table className={styles.itemizedTable}>
                            {itemizedBill?.map((item, index) =>
                                <tr key={index}>
                                    <td>
                                        {item.applicationNumber}
                                    </td>
                                    <td>
                                        {item.applicationName}
                                    </td>
                                    <td>
                                        ${item.applicationFee ?? 0}
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td></td>
                                <td className={styles.itemizedBillTotal}>
                                    {t('Payment.TotalDue')}
                                </td>
                                <td className={styles.itemizedBillTotal}>
                                    ${amount.toFixed(2)}
                                </td>
                            </tr>
                        </table>
                    </Panel>
                </PanelRow>
                : null}

            {coreIframeHtml && (
                <PanelRow>
                    <Panel>
                        <div className="topOfIframe" ref={topOfIframeRef} />
                        <CoreBillPayForm html={coreIframeHtml} />
                        {paymentSuccess && (
                            <div className={styles.paymentConfirmationContainer}>
                                <Button
                                    onClick={() => {
                                        history.push('/dashboard');
                                    }}
                                    color={'blue'}
                                >
                                    {t('Util.Finish')}
                                </Button>
                            </div>
                        )}
                    </Panel>
                </PanelRow>
            )}
        </PageContainer>
    );
};
